import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "./context/AuthContext";
import { LocationProvider } from "./context/LocationContext";
import { AdminProvider } from "./context/AdminContext";
import { CallStateProvider } from "./context/CallStateContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import RoleBasedRoute from "./components/auth/RoleBasedRoute";
import ErrorBoundary from "./components/common/ErrorBoundary";
import SingleTabGuard from "./components/common/SingleTabGuard";
import { PERMISSIONS } from "./config/rolePermissions";
import Layout from "./components/layout/Layout";
import { TwilioProvider } from "./context/TwilioContext";

const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("./pages/auth/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/auth/ForgotPasswordPage"));
const PortalAccessPage = lazy(() => import("./pages/auth/PortalAccessPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const AdminDashboardPage = lazy(() => import("./pages/admin/AdminDashboardPage"));
const AdminOrganizationsPage = lazy(() =>
  import("./pages/admin/AdminOrganizationsPage")
);
const AdminUsersPage = lazy(() => import("./pages/admin/AdminUsersPage"));
const AdminInquiriesPage = lazy(() => import("./pages/admin/AdminInquiriesPage"));
const AdminAgentsPage = lazy(() => import("./pages/admin/AdminAgentsPage"));
const AgentDashboardPage = lazy(() => import("./pages/agent/AgentDashboardPage"));
const LeadQueuePage = lazy(() => import("./pages/agent/LeadQueuePage"));
const CallHistoryPage = lazy(() => import("./pages/agent/CallHistoryPage"));
const MyTemplatesPage = lazy(() => import("./pages/agent/MyTemplatesPage"));
const TeamLeaderDashboardPage = lazy(() =>
  import("./pages/team-leader/TeamLeaderDashboardPage")
);
const MyLocationsPage = lazy(() => import("./pages/team-leader/MyLocationsPage"));
const MyAgentsPage = lazy(() => import("./pages/team-leader/MyAgentsPage"));
const ScriptsPage = lazy(() => import("./pages/team-leader/ScriptsPage"));
const TLCallHistoryPage = lazy(() => import("./pages/team-leader/TLCallHistoryPage"));
const ReportsPage = lazy(() => import("./pages/reports/ReportsPage"));
const AppointmentsPage = lazy(() => import("./pages/appointments/AppointmentsPage"));
const ConsultationsPage = lazy(() => import("./pages/consultations/ConsultationsPage"));
const ConsultationDetailPage = lazy(() =>
  import("./pages/consultations/ConsultationDetailPage")
);
const RecordingPage = lazy(() => import("./pages/consultations/RecordingPage"));
const FollowUpsPage = lazy(() => import("./pages/followups/FollowUpsPage"));
const ConversationsPage = lazy(() => import("./pages/conversations/ConversationsPage"));
const ConversationDetailPage = lazy(() =>
  import("./pages/conversations/ConversationDetailPage")
);
const TasksPage = lazy(() => import("./pages/tasks/TasksPage"));
const CoachingPage = lazy(() => import("./pages/coaching/CoachingPage"));
const PipelinePage = lazy(() => import("./pages/pipeline/PipelinePage"));
const AnalyticsPage = lazy(() => import("./pages/analytics/AnalyticsPage"));
const ReactivationPage = lazy(() => import("./pages/reactivation/ReactivationPage"));
const RecareDashboardPage = lazy(() => import("./pages/recare/RecareDashboardPage"));
const SettingsPage = lazy(() => import("./pages/settings/SettingsPage"));
const OrganizationSettingsPage = lazy(() =>
  import("./pages/settings/OrganizationSettingsPage")
);
const LocationsPage = lazy(() => import("./pages/settings/LocationsPage"));
const UsersPage = lazy(() => import("./pages/settings/UsersPage"));
const PMSIntegrationPage = lazy(() => import("./pages/settings/PMSIntegrationPage"));
const TreatmentsPage = lazy(() => import("./pages/settings/TreatmentsPage"));
const ProfileSettingsPage = lazy(() => import("./pages/settings/ProfileSettingsPage"));
const AdminLocationsPage = lazy(() => import("./pages/admin/AdminLocationsPage"));
const StagesPage = lazy(() => import("./pages/stages/StagesPage"));
const SmilePreviewPage = lazy(() => import("./pages/smile-preview/SmilePreviewPage"));
const TicketsPage = lazy(() => import("./pages/tickets/TicketsPage"));
const AdminTicketsPage = lazy(() => import("./pages/admin/AdminTicketsPage"));
const AdminRecycleBinPage = lazy(() => import("./pages/admin/AdminRecycleBinPage"));
const SuperAdminManualPage = lazy(() => import("./pages/manuals/SuperAdminManualPage"));
const OwnerManualPage = lazy(() => import("./pages/manuals/OwnerManualPage"));
const FrontDeskManualPage = lazy(() => import("./pages/manuals/FrontDeskManualPage"));
const TreatmentCoordinatorManualPage = lazy(() =>
  import("./pages/manuals/TreatmentCoordinatorManualPage")
);
const AgentManualPage = lazy(() => import("./pages/manuals/AgentManualPage"));
const TeamLeaderManualPage = lazy(() => import("./pages/manuals/TeamLeaderManualPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

function RoutePageFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center bg-surface-50">
      <div
        className="h-8 w-8 shrink-0 rounded-full border-2 border-brand-400 border-t-transparent animate-spin"
        aria-label="Loading page"
        role="status"
      />
    </div>
  );
}

/** Public routes are not under Layout; keep Suspense local to those pages only. */
function PublicLazyPage({ children }) {
  return <Suspense fallback={<RoutePageFallback />}>{children}</Suspense>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicLazyPage>
            <LoginPage />
          </PublicLazyPage>
        }
      />
      <Route
        path="/register"
        element={
          <PublicLazyPage>
            <RegisterPage />
          </PublicLazyPage>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicLazyPage>
            <ForgotPasswordPage />
          </PublicLazyPage>
        }
      />
      <Route
        path="/portal-access"
        element={
          <PublicLazyPage>
            <PortalAccessPage />
          </PublicLazyPage>
        }
      />

      {/* Protected routes with layout */}
      <Route
        element={
          <ProtectedRoute>
            <AdminProvider>
              <LocationProvider>
                <TwilioProvider>
                  <CallStateProvider>
                    <ErrorBoundary variant="page">
                      <Layout />
                    </ErrorBoundary>
                  </CallStateProvider>
                </TwilioProvider>
              </LocationProvider>
            </AdminProvider>
          </ProtectedRoute>
        }
      >
        {/* Dashboard - All roles */}
        <Route path="/dashboard" element={<ErrorBoundary variant="section"><DashboardPage /></ErrorBoundary>} />
        <Route
          path="/call-history"
          element={
            <RoleBasedRoute allowedRoles={["front_desk"]}>
              <ErrorBoundary variant="section">
                <CallHistoryPage variant="front_desk" />
              </ErrorBoundary>
            </RoleBasedRoute>
          }
        />

        {/* Admin Dashboard - Super Admin Only */}
        <Route
          path="/admin"
          element={
            <RoleBasedRoute allowedRoles={["super_admin"]}>
              <AdminDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/organizations"
          element={
            <RoleBasedRoute allowedRoles={["super_admin"]}>
              <AdminOrganizationsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/logs"
          element={
            <RoleBasedRoute allowedRoles={["super_admin"]}>
              <AdminLocationsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RoleBasedRoute allowedRoles={["super_admin"]}>
              <AdminUsersPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/inquiries"
          element={
            <RoleBasedRoute allowedRoles={["super_admin"]}>
              <AdminInquiriesPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/agents"
          element={
            <RoleBasedRoute allowedRoles={["super_admin"]}>
              <AdminAgentsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/tickets"
          element={
            <RoleBasedRoute allowedRoles={["super_admin"]}>
              <AdminTicketsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/admin/manual"
          element={
            <RoleBasedRoute allowedRoles={["super_admin"]}>
              <ErrorBoundary variant="section">
                <SuperAdminManualPage />
              </ErrorBoundary>
            </RoleBasedRoute>
          }
        />
        <Route
          path="/owner/manual"
          element={
            <RoleBasedRoute allowedRoles={["owner"]}>
              <ErrorBoundary variant="section">
                <OwnerManualPage />
              </ErrorBoundary>
            </RoleBasedRoute>
          }
        />
        <Route
          path="/front-desk/manual"
          element={
            <RoleBasedRoute allowedRoles={["front_desk"]}>
              <ErrorBoundary variant="section">
                <FrontDeskManualPage />
              </ErrorBoundary>
            </RoleBasedRoute>
          }
        />
        <Route
          path="/treatment-coordinator/manual"
          element={
            <RoleBasedRoute allowedRoles={["treatment_coordinator"]}>
              <ErrorBoundary variant="section">
                <TreatmentCoordinatorManualPage />
              </ErrorBoundary>
            </RoleBasedRoute>
          }
        />

        {/* Recycle Bin (Super Admins only) */}
        <Route
          path="/recycle-bin"
          element={
            <RoleBasedRoute allowedRoles={["super_admin"]}>
              <ErrorBoundary variant="section">
                <AdminRecycleBinPage />
              </ErrorBoundary>
            </RoleBasedRoute>
          }
        />

        {/* Agent Pages */}
        <Route
          path="/agent"
          element={
            <RoleBasedRoute allowedRoles={["agent"]}>
              <AgentDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/agent/lead-queue"
          element={
            <RoleBasedRoute allowedRoles={["agent"]}>
              <LeadQueuePage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/agent/my-templates"
          element={
            <RoleBasedRoute
              allowedRoles={["agent", "team_leader", "front_desk"]}
            >
              <MyTemplatesPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/agent/call-history"
          element={
            <RoleBasedRoute allowedRoles={["agent"]}>
              <CallHistoryPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/agent/manual"
          element={
            <RoleBasedRoute allowedRoles={["agent"]}>
              <ErrorBoundary variant="section">
                <AgentManualPage />
              </ErrorBoundary>
            </RoleBasedRoute>
          }
        />

        {/* Team Leader Pages */}
        <Route
          path="/team-leader"
          element={
            <RoleBasedRoute allowedRoles={["team_leader"]}>
              <TeamLeaderDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/team-leader/locations"
          element={
            <RoleBasedRoute allowedRoles={["team_leader"]}>
              <MyLocationsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/team-leader/agents"
          element={
            <RoleBasedRoute allowedRoles={["team_leader"]}>
              <MyAgentsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/team-leader/scripts"
          element={
            <RoleBasedRoute allowedRoles={["team_leader"]}>
              <ScriptsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/team-leader/call-history"
          element={
            <RoleBasedRoute allowedRoles={["team_leader"]}>
              <TLCallHistoryPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/team-leader/manual"
          element={
            <RoleBasedRoute allowedRoles={["team_leader"]}>
              <ErrorBoundary variant="section">
                <TeamLeaderManualPage />
              </ErrorBoundary>
            </RoleBasedRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <RoleBasedRoute allowedRoles={["agent", "team_leader", "super_admin"]}>
              <ErrorBoundary variant="section">
                <ReportsPage />
              </ErrorBoundary>
            </RoleBasedRoute>
          }
        />

        {/* Support Tickets (Org users) */}
        <Route
          path="/tickets"
          element={
            <RoleBasedRoute
              requiredPermissions={[
                PERMISSIONS.CREATE_TICKET,
                PERMISSIONS.VIEW_ORG_TICKETS,
                PERMISSIONS.VIEW_OWN_TICKETS,
              ]}
            >
              <TicketsPage />
            </RoleBasedRoute>
          }
        />

        {/* Appointments - All roles with VIEW_APPOINTMENTS */}
        <Route path="/appointments" element={<ErrorBoundary variant="section"><AppointmentsPage /></ErrorBoundary>} />

        {/* Recare Workflow — removed, Recare Dashboard covers all functionality */}

        {/* Recare Dashboard - Owner + Super Admin */}
        <Route
          path="/recare-dashboard"
          element={
            <RoleBasedRoute allowedRoles={["owner", "super_admin"]}>
              <RecareDashboardPage />
            </RoleBasedRoute>
          }
        />

        {/* Consultations */}
        <Route path="/consultations" element={<ErrorBoundary variant="section"><ConsultationsPage /></ErrorBoundary>} />
        <Route path="/consultations/:id" element={<ErrorBoundary variant="section"><ConsultationDetailPage /></ErrorBoundary>} />
        <Route
          path="/recording/:appointmentId"
          element={
            <RoleBasedRoute
              allowedRoles={["owner", "treatment_coordinator"]}
            >
              <RecordingPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/recording"
          element={
            <RoleBasedRoute
              allowedRoles={["owner", "treatment_coordinator"]}
            >
              <RecordingPage />
            </RoleBasedRoute>
          }
        />

        {/* Follow-ups - TC, Admin, Owner */}
        <Route
          path="/follow-ups"
          element={
            <RoleBasedRoute requiredPermissions={PERMISSIONS.VIEW_FOLLOW_UPS}>
              <FollowUpsPage />
            </RoleBasedRoute>
          }
        />

        {/* Smile Preview - AI-Powered Dental Transformation */}
        <Route
          path="/smile-preview"
          element={
            <RoleBasedRoute
              allowedRoles={[
                "owner",
                "treatment_coordinator",
              ]}
            >
              <SmilePreviewPage />
            </RoleBasedRoute>
          }
        />

        {/* Conversations */}
        <Route
          path="/conversations"
          element={
            <RoleBasedRoute
              requiredPermissions={PERMISSIONS.VIEW_CONVERSATIONS}
            >
              <ConversationsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/conversations/:id"
          element={
            <RoleBasedRoute
              requiredPermissions={PERMISSIONS.VIEW_CONVERSATIONS}
            >
              <ConversationDetailPage />
            </RoleBasedRoute>
          }
        />

        {/* Tasks - All staff */}
        <Route
          path="/tasks"
          element={
            <RoleBasedRoute
              requiredPermissions={[
                PERMISSIONS.VIEW_ALL_TASKS,
                PERMISSIONS.VIEW_OWN_TASKS,
              ]}
            >
              <TasksPage />
            </RoleBasedRoute>
          }
        />

        {/* Coaching - Personal for TCs, full for admin/owner */}
        <Route
          path="/coaching"
          element={
            <RoleBasedRoute
              requiredPermissions={[
                PERMISSIONS.VIEW_OWN_COACHING,
                PERMISSIONS.VIEW_ALL_COACHING,
              ]}
            >
              <CoachingPage />
            </RoleBasedRoute>
          }
        />

        {/* Pipeline - Admin, Owner */}
        <Route
          path="/pipeline"
          element={
            <RoleBasedRoute requiredPermissions={PERMISSIONS.VIEW_PIPELINE}>
              <PipelinePage />
            </RoleBasedRoute>
          }
        />

        {/* Analytics - Admin, Owner */}
        <Route
          path="/analytics"
          element={
            <RoleBasedRoute requiredPermissions={PERMISSIONS.VIEW_ANALYTICS}>
              <AnalyticsPage />
            </RoleBasedRoute>
          }
        />

        {/* Reactivation - Admin, Owner */}
        <Route
          path="/reactivation"
          element={
            <RoleBasedRoute requiredPermissions={PERMISSIONS.VIEW_REACTIVATION}>
              <ReactivationPage />
            </RoleBasedRoute>
          }
        />

        {/* Settings */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route
          path="/settings/organization"
          element={
            <RoleBasedRoute
              requiredPermissions={PERMISSIONS.MANAGE_ORGANIZATION}
            >
              <OrganizationSettingsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/settings/locations"
          element={
            <RoleBasedRoute requiredPermissions={[PERMISSIONS.MANAGE_LOCATIONS, PERMISSIONS.VIEW_ALL_LOCATIONS]}>
              <LocationsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/settings/users"
          element={
            <RoleBasedRoute requiredPermissions={PERMISSIONS.MANAGE_USERS}>
              <UsersPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/settings/pms"
          element={
            <RoleBasedRoute requiredPermissions={PERMISSIONS.MANAGE_PMS}>
              <PMSIntegrationPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/treatments"
          element={
            <RoleBasedRoute allowedRoles={["owner"]}>
              <TreatmentsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="/stages"
          element={
            <RoleBasedRoute allowedRoles={["owner"]}>
              <StagesPage />
            </RoleBasedRoute>
          }
        />
        <Route path="/settings/profile" element={<ProfileSettingsPage />} />
        <Route
          path="*"
          element={
            <ErrorBoundary variant="section">
              <NotFoundPage embedded />
            </ErrorBoundary>
          }
        />
      </Route>

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-surface-50 text-surface-900">
          <SingleTabGuard>
            <AppRoutes />
          </SingleTabGuard>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: "#fff",
                color: "#0f172a",
                boxShadow:
                  "0 10px 40px -10px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
                borderRadius: "16px",
                padding: "16px 20px",
                fontSize: "14px",
                fontWeight: "500",
              },
              success: {
                iconTheme: {
                  primary: "#10b981",
                  secondary: "#fff",
                },
                style: {
                  borderLeft: "4px solid #10b981",
                },
              },
              error: {
                iconTheme: {
                  primary: "#f43f5e",
                  secondary: "#fff",
                },
                style: {
                  borderLeft: "4px solid #f43f5e",
                },
              },
              loading: {
                iconTheme: {
                  primary: "#14b8a6",
                  secondary: "#fff",
                },
              },
            }}
          />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
